import { Box, LoadingOverlay, Text } from "@mantine/core"
import { ArrowCounterClockwiseIcon, PercentIcon, ReceiptIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import classes from "../analytics.module.css"
import { DrillTable, DrillTitle } from "../cards/AnalyticsCard"
import { KpiCard } from "../cards/KpiCard"
import { RankBadge, RankColumn, RankTable } from "../cards/RankTable"
import { SectionHeader } from "../cards/SectionHeader"
import {
  ReturnsByCustomer,
  ReturnsByCustomerRow,
  ReturnsByEmployee,
  ReturnsByEmployeeRow,
  ReturnsByProduct,
  ReturnsByProductRow,
  ReturnsSummary
} from "../types"
import { formatCompactMnt, formatDecimal, formatDelta, formatInt, formatMnt, formatMonthDay, formatPct } from "../utils"

type LoadingProps<T> = { data: T | undefined; isLoading: boolean }

const fmtMnt = formatMnt

// ============ Summary section ============

export function ReturnsSummarySection({ data, isLoading }: LoadingProps<ReturnsSummary>) {
  const { t } = useTranslation(["dashboard"])
  const total = data?.data.totalReturnAmount
  const count = data?.data.returnCount
  const rate  = data?.data.returnRatePct

  const totalFmt = total ? formatCompactMnt(total.value) : { display: "—", unit: "" }
  const totalMom = formatPct(total?.momPct ?? null)
  const countDelta = formatDelta(count?.momDelta ?? null, 0)
  const rateDelta  = formatDelta(rate?.momDelta ?? null, 1)

  return (
    <Box pos="relative" className={classes.section}>
      <LoadingOverlay visible={isLoading} />
      <SectionHeader
        title={t("analytics.sections.returns.title", { defaultValue: "Буцаалт" })}
        helpKey="order.dashboard.section.analytics-returns"
        description={t("analytics.sections.returns.desc", { defaultValue: "Буцаалтын дүн шинжилгээ" })}
      />
      <Box className={classes.kpiGrid} data-cols={3}>
        <KpiCard
          icon={ReceiptIcon}
          helpKey="order.dashboard.field.analytics-return-amount"
          label={t("analytics.returns.totalAmount", { defaultValue: "Нийт буцаалтын дүн" })}
          value={totalFmt.display}
          unit={totalFmt.unit}
          changes={[{ sign: totalMom.sign, text: totalMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }]}
        />
        <KpiCard
          icon={ArrowCounterClockwiseIcon}
          helpKey="order.dashboard.field.analytics-return-count"
          label={t("analytics.returns.count", { defaultValue: "Буцаалтын тоо" })}
          value={count ? formatInt(count.value) : "—"}
          unit={t("analytics.tables.pieces", { defaultValue: "ш" })}
          changes={[{ sign: countDelta.sign, text: countDelta.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }]}
        />
        <KpiCard
          icon={PercentIcon}
          helpKey="order.dashboard.field.analytics-return-rate"
          label={t("analytics.returns.ratePct", { defaultValue: "Буцаалтын хувь" })}
          value={rate ? formatDecimal(rate.value, 1) : "—"}
          unit="%"
          changes={[{ sign: rateDelta.sign, text: rateDelta.sign === "flat" ? rateDelta.text : `${rateDelta.text}%`, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }]}
        />
      </Box>
    </Box>
  )
}

// ============ Returns by product ============

export function ReturnsByProductCard({ data, isLoading }: LoadingProps<ReturnsByProduct>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const columns: RankColumn<ReturnsByProductRow>[] = [
    { key: "rank", header: "#", width: "56px", render: (_, idx) => <RankBadge n={idx + 1} /> },
    { key: "name", header: t("analytics.tables.product", { defaultValue: "Бараа" }), render: r => r.productName },
    { key: "qty",  header: t("analytics.tables.qty", { defaultValue: "Тоо" }), render: r => `${formatInt(r.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}` },
    { key: "amount", header: t("analytics.tables.amount", { defaultValue: "Дүн" }), align: "right", render: r => <Text component="span" className={classes.amount}>{fmtMnt(r.amount)}</Text> }
  ]
  return (
    <RankTable<ReturnsByProductRow>
      title={t("analytics.returns.byProduct", { defaultValue: "Буцаалт бараагаар" })}
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      rowKey={r => r.productId}
      renderDrill={r => (
        <>
          <DrillTitle>{r.productName} · {t("analytics.returns.breakdown", { defaultValue: "Буцаалтын задаргаа" })}</DrillTitle>
          <DrillTable
            headers={[t("analytics.tables.partner", { defaultValue: "Харилцагч" }), t("analytics.returns.reason", { defaultValue: "Шалтгаан" }), t("analytics.tables.date", { defaultValue: "Огноо" })]}
            rows={r.breakdown.map(b => [b.partnerName, b.reasonLabel, formatMonthDay(b.date)])}
          />
        </>
      )}
    />
  )
}

// ============ Returns by customer ============

export function ReturnsByCustomerCard({ data, isLoading }: LoadingProps<ReturnsByCustomer>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const columns: RankColumn<ReturnsByCustomerRow>[] = [
    { key: "rank", header: "#", width: "56px", render: (_, idx) => <RankBadge n={idx + 1} /> },
    { key: "name", header: t("analytics.tables.partner", { defaultValue: "Харилцагч" }), render: r => r.partnerName },
    { key: "qty",  header: t("analytics.tables.qty", { defaultValue: "Тоо" }), render: r => `${formatInt(r.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}` },
    { key: "amount", header: t("analytics.tables.amount", { defaultValue: "Дүн" }), align: "right", render: r => <Text component="span" className={classes.amount}>{fmtMnt(r.amount)}</Text> }
  ]
  return (
    <RankTable<ReturnsByCustomerRow>
      title={t("analytics.returns.byCustomer", { defaultValue: "Буцаалт харилцагчаар" })}
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      rowKey={r => r.partnerId}
      renderDrill={r => (
        <>
          <DrillTitle>{r.partnerName} · {t("analytics.returns.returnedItems", { defaultValue: "Буцаалсан бараанууд" })}</DrillTitle>
          <DrillTable
            headers={[t("analytics.tables.product", { defaultValue: "Бараа" }), t("analytics.tables.qty", { defaultValue: "Тоо" }), t("analytics.returns.reason", { defaultValue: "Шалтгаан" }), t("analytics.tables.amount", { defaultValue: "Дүн" })]}
            rows={r.breakdown.map(b => [
              b.productName,
              `${formatInt(b.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}`,
              <Text key="r" component="span" c="dimmed">{b.reasonLabel}</Text>,
              fmtMnt(b.amount)
            ])}
          />
        </>
      )}
    />
  )
}

// ============ Returns by employee ============

export function ReturnsByEmployeeCard({ data, isLoading }: LoadingProps<ReturnsByEmployee>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const columns: RankColumn<ReturnsByEmployeeRow>[] = [
    { key: "rank", header: "#", width: "56px", render: (_, idx) => <RankBadge n={idx + 1} /> },
    { key: "name", header: t("analytics.margin.employee", { defaultValue: "Ажилтан" }), render: r => r.name },
    { key: "qty",  header: t("analytics.tables.qty", { defaultValue: "Тоо" }), render: r => `${formatInt(r.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}` },
    { key: "amount", header: t("analytics.tables.amount", { defaultValue: "Дүн" }), align: "right", render: r => <Text component="span" className={classes.amount}>{fmtMnt(r.amount)}</Text> }
  ]
  return (
    <RankTable<ReturnsByEmployeeRow>
      title={t("analytics.returns.byEmployee", { defaultValue: "Буцаалт ажилтнаар" })}
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      rowKey={r => r.userId}
      renderDrill={r => (
        <>
          <DrillTitle>{r.name} · {t("analytics.returns.handled", { defaultValue: "Хариуцсан буцаалтууд" })}</DrillTitle>
          <DrillTable
            headers={[t("analytics.tables.partner", { defaultValue: "Харилцагч" }), t("analytics.tables.product", { defaultValue: "Бараа" }), t("analytics.returns.reason", { defaultValue: "Шалтгаан" }), t("analytics.tables.amount", { defaultValue: "Дүн" })]}
            rows={r.breakdown.map(b => [b.partnerName, b.productName, <Text key="r" component="span" c="dimmed">{b.reasonLabel}</Text>, fmtMnt(b.amount)])}
          />
        </>
      )}
    />
  )
}
